import React, { useState, useEffect, useMemo } from "react";
import "./details.css"; // Import the CSS file
import {
  Form,
  Input,
  Select,
  Descriptions,
  Spin,
  InputNumber,
  Radio,
  Tooltip,
  Upload,
  Button,
} from "antd";
import { UploadChangeParam } from "antd/es/upload";
import { getAllSupplierOrg } from "../../../services/DataTransferAPI/get-all-supplier-org";
import { getCurrentDTSVersion } from "../../../services/DataTransferAPI/get-current-dts-version";
import {
  DTSTransferMessage,
  validateDTSVersion,
  changeOfDescriptionMessage,
  validateDataFormat,
  shouldShowDataFormat,
} from "./utils";
import { useOpenNotification } from "../../../components/Notification/notification";
import UploadDTS from "../UploadDTS/upload-dts";
import DeliverablesReview from "../UploadDTS/deliverables-review";
import { PaperClipOutlined, UploadOutlined } from "@ant-design/icons";

const { TextArea } = Input;

interface DetailsProps {
  optionsValue: any[];
  msdValue: any[];
  msdDataLoading: boolean;
  formRef?: any;
  nextPage: string;
  detailsSectionData?: {
    MSDCompound?: string;
    supplierOrganization?: string;
    supplierCompound?: string;
    DTSVersion?: string;
    transferId?: string;
    MSDCompoundType?: string;
    descriptionChange?: string;
    [key: string]: any;
  };
  handleDeliverablesDescription: (data: any) => void;
  uploadDTSFormValue: any;
  status: string;
  isEditing?: boolean;
  showFlowToggle?: boolean;
  isOfflineFlow?: boolean;
  onFlowToggleChange?: (nextFlowIsOffline: boolean) => void;
}

const Details: React.FC<DetailsProps> = ({
  optionsValue,
  msdValue,
  msdDataLoading,
  formRef,
  nextPage,
  detailsSectionData,
  handleDeliverablesDescription,
  uploadDTSFormValue,
  status,
  isEditing = false,
  showFlowToggle = false,
  isOfflineFlow = false,
  onFlowToggleChange,
}) => {
  const [supplierOptionsValue, setSupplierOptionsValue] = useState<any[]>([]);
  const [supplierLoading, setSupplierLoading] = useState(false);
  const [disableDTS, setDisableDTS] = useState(true);
  const [disableChangeDescription, setDisableChangeDescription] = useState(true);
  const [isPageLoad, setIsPageLoad] = useState<boolean>(false);
  const [activeDTSVersion, setActiveDTSVersion] = useState<number>(0);
  const [activeDTSVersionStatus, setActiveDTSVersionStatus] =
    useState<string>("");
  const [isDTSVersionPresent, setIsDTSVersionPresent] =
    useState<boolean>(false);
  const [hasMultipleRecords, setHasMultipleRecords] = useState<boolean>(false);
  const { openNotification, contextHolder } = useOpenNotification();
  const [dtsFieldErrorCount, setDtsFieldErrorCount] = useState<number>(0);
  const [transferId, setTransferId] = useState<string>("");
  const formValues = Form.useWatch([], formRef) as any;
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [fileList, setFileList] = useState<any[]>([]);
  const dataFormatVisible = useMemo(
    () =>
      shouldShowDataFormat(status, isEditing)
        ? (detailsSectionData?.dataFormat ?? [])
        : [],
    [status, isEditing, detailsSectionData?.dataFormat],
  );

  const MSDCompoundODMOptions = Array.isArray(msdValue)
    ? (msdValue?.map((item: { studyName: any; studyNumber: any }) => ({
        value: item?.studyName,
        studyNumber: item?.studyNumber,
      })) ?? [])
    : [];

  const handleInitialization = (value: boolean) => {
    setIsInitialized(true);
  };

  const handleChange = async (info: UploadChangeParam) => {
    const { fileList: newFileList } = info;
    setFileList(newFileList);
    formRef.setFieldsValue({ dataFormat: newFileList });
    formRef?.validateFields(["dataFormat"]);
  };

  const transferIdOptions = Array.isArray(optionsValue)
    ? optionsValue.map((item: any) => ({
      value: item.transfer_id,
      label: (
        <Tooltip
          title={
            <div>
              <strong>{item.transfer_id}</strong>
              <br />
              {(item.description || "")
                .split("\n")
                .map((line: string, index: number) => (
                  <React.Fragment key={index}>
                    {line}
                    <br />
                  </React.Fragment>
                ))}
            </div>
          }
          placement="right"
          overlayInnerStyle={{
            backgroundColor: "white",
            color: "black",
          }}
        >
          <div style={{ width: "100%" }}>{item.transfer_id}</div>
        </Tooltip>
      ),
      }))
    : [];

  const MSDCompoundTypeSelection = (event: any) => {
    formRef?.setFieldsValue({ MSDCompound: undefined });
  };

  const supplierOrgOptions = useMemo(
    () =>
      Array.isArray(supplierOptionsValue)
        ? supplierOptionsValue.map((item: any) => ({
            label: item,
            value: item,
            key: item,
          }))
        : [],
    [supplierOptionsValue],
  );

  useEffect(() => {
    const fetchAllSupplierOrg = async () => {
      setSupplierLoading(true);
      try {
        const response = await getAllSupplierOrg();
        if ((response as { data?: any })?.data) {
          let msdOrganizations = response?.data as string[];
          if (!msdOrganizations.includes("MSD")) {
            msdOrganizations = ["MSD", ...msdOrganizations];
          }
          const sortedData = msdOrganizations
            .slice()
            .sort((a: string, b: string) => a.localeCompare(b));
          setSupplierOptionsValue(sortedData);
        }
      } finally {
        setSupplierLoading(false);
      }
    };
    fetchAllSupplierOrg();
  }, []);

  const handleTransferIdChange = (value: string) => {
    setTransferId(value);
  };

  useEffect(() => {
    if (detailsSectionData && Object.keys(detailsSectionData).length !== 0) {
      formRef?.setFieldsValue({
        MSDCompound: detailsSectionData?.MSDCompound ?? "",
        supplierOrganization: detailsSectionData?.supplierOrganization ?? "",
        supplierCompound: detailsSectionData?.supplierCompound ?? "",
        DTSVersion: detailsSectionData?.DTSVersion
          ? parseInt(detailsSectionData?.DTSVersion, 10)
          : "",
        transferId: detailsSectionData?.transferId ?? "",
        MSDCompoundType: detailsSectionData?.MSDCompoundType ?? "",
        descriptionChange: detailsSectionData?.descriptionChange,
        purpose: detailsSectionData?.purpose ?? "",
        dataFormat: dataFormatVisible,
        signatureMethod: isOfflineFlow ? "offline" : "online",
      });
      setTransferId(detailsSectionData?.transferId ?? "");
      setIsDTSVersionPresent(true);
      setDisableDTS(false);
      setFileList(dataFormatVisible);
    } else {
      formRef?.setFieldsValue({
        MSDCompoundType: "ODM",
        signatureMethod: isOfflineFlow ? "offline" : "online",
      });
    }
    setIsPageLoad(true);
  }, [detailsSectionData, formRef, status, dataFormatVisible, isOfflineFlow]);

  useEffect(() => {
    formRef?.setFieldsValue({
      signatureMethod: isOfflineFlow ? "offline" : "online",
    });
  }, [formRef, isOfflineFlow]);

  useEffect(() => {
    const MSDCompound = formRef?.getFieldsValue()?.MSDCompound;
    if (
      formRef?.getFieldsValue()?.transferId &&
      MSDCompound &&
      MSDCompound.length > 7 &&
      formRef?.getFieldsValue()?.supplierOrganization
    ) {
      setDisableDTS(true);
      !isPageLoad && formRef?.setFieldsValue({ DTSVersion: undefined });
      getCurrentDTSVersion(
        formRef?.getFieldsValue()?.MSDCompound,
        formRef?.getFieldsValue()?.supplierOrganization,
        formRef?.getFieldsValue()?.transferId,
      )
        .then((response) => {
          const dtsVersion = response?.dts_version
            ? parseInt(response?.dts_version, 10)
            : 0;
          setActiveDTSVersion(dtsVersion);
          setActiveDTSVersionStatus(response?.status ?? "");
          setHasMultipleRecords(response?.has_multiple_records);
          setDisableDTS(false);
          setIsPageLoad(false);
        })
        .catch((error) => {
          openNotification("error", "Failed to fetch last active DTS Version");
        });
    } else {
      setDisableDTS(true);
    }
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [
    formRef,
    formRef?.getFieldsValue()?.transferId,
    formRef?.getFieldsValue()?.MSDCompound,
    formRef?.getFieldsValue()?.supplierOrganization,
    openNotification,
  ]);

  useEffect(() => {
    if (nextPage === "data-transfer" && parseInt(detailsSectionData?.DTSVersion ?? "0", 10) !== parseInt(formRef?.getFieldsValue()?.DTSVersion ?? "0", 10)) {
      setDisableChangeDescription(true);
      formRef?.setFieldsValue({ descriptionChange: undefined });
    }
  }, [nextPage, formRef?.getFieldsValue()?.DTSVersion, detailsSectionData]);

  useEffect(() => {
    const dtsVersion = parseInt(formRef?.getFieldsValue()?.DTSVersion ?? "0", 10);
    const descriptionChange = formRef?.getFieldsValue()?.descriptionChange;
    setDisableChangeDescription(true);
    if (dtsVersion === 1) {
      formRef?.setFieldsValue({ descriptionChange: "Original publication" });
    } else if (dtsVersion > 1) {
      if (activeDTSVersion === 0) {
        setDisableChangeDescription(false);
        formRef?.setFieldsValue({
          descriptionChange:
            descriptionChange === "Original publication" || !descriptionChange
              ? "First publication created through CDIG"
              : descriptionChange,
        });
      } else if (activeDTSVersion > 0) {
        setDisableChangeDescription(false);
      }

      if (
        parseInt(detailsSectionData?.DTSVersion ?? "0", 10) !== dtsVersion &&
        activeDTSVersionStatus === "Save Draft" || activeDTSVersionStatus === "Admin creates draft"
      ) {
        setDisableChangeDescription(false);
        formRef?.setFieldsValue({ descriptionChange: hasMultipleRecords ? undefined : "First publication created through CDIG" });
      }

      if(
        parseInt(detailsSectionData?.DTSVersion ?? "0", 10) === dtsVersion &&
        formRef?.getFieldsValue()?.MSDCompound === detailsSectionData?.MSDCompound &&
        formRef?.getFieldsValue()?.supplierOrganization === detailsSectionData?.supplierOrganization &&
        formRef?.getFieldsValue()?.transferId === detailsSectionData?.transferId
      ) {
        setDisableChangeDescription(false);
        formRef?.setFieldsValue({ descriptionChange: detailsSectionData?.descriptionChange ?? undefined });
      }
    }

    if (dtsFieldErrorCount) {
      formRef?.setFieldsValue({ descriptionChange: undefined });
      setDisableChangeDescription(true);
    }
  }, [formRef?.getFieldsValue()?.DTSVersion, formRef, activeDTSVersion, dtsFieldErrorCount]);

  const dtsVersionValue = detailsSectionData?.DTSVersion
    ? parseFloat(detailsSectionData?.DTSVersion).toFixed(1)
    : "";

  // Prepare the details fields for Descriptions
  const trialDetailsFieldsValue = [
    {
      key: "1",
      label: "Source of MSD Compound and Protocol Number/Identifier",
      children: detailsSectionData?.MSDCompoundType ?? "",
    },
    {
      key: "2",
      label: "MSD Compound and Protocol Number/Identifier",
      children: detailsSectionData?.MSDCompound ?? "",
    },
    {
      key: "3",
      label: "Supplier",
      children: detailsSectionData?.supplierOrganization ?? "",
    },
    {
      key: "4",
      label: "Supplier Compound and Protocol Number/Identifier",
      children: detailsSectionData?.supplierCompound ?? "",
    },
  ];

  const transferInformationFieldsValue = [
    {
      key: "5",
      label: "Transfer ID",
      children: detailsSectionData?.transferId ?? "",
    },
    { key: "6", label: "DTS Version", children: dtsVersionValue ?? "" },
    {
      key: "7",
      label: "Description of Change",
      children: detailsSectionData?.descriptionChange ?? "",
    },
    {
      key: "8",
      label: "Purpose",
      children: detailsSectionData?.purpose ?? "",
    },
    {
      key: "9",
      label: "Data Format",
      children: detailsSectionData?.dataFormat?.length ? <><PaperClipOutlined /> {detailsSectionData?.dataFormat[0].name}</> : "",
    },
  ];
  return (
    <>
      {nextPage === "data-transfer" ? (
        <div id="data-transfer-details-form">
          {showFlowToggle && (
            <div className="flow-toggle-section" style={{ padding: "20px", backgroundColor: "white", marginBottom: "16px" }}>
              <div style={{ fontSize: "25px", marginBottom: 10 }}>DTS Agreement Details</div>
              <Form.Item
                className="item-width-50"
                label="Signature Method"
                name="signatureMethod"
                required
                rules={[{ required: true, message: "This is a required field" }]}
              >
                <Radio.Group
                  value={isOfflineFlow ? "offline" : "online"}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                  }}
                  onChange={(event) => {
                    onFlowToggleChange?.(event.target.value === "offline");
                  }}
                >
                  <Radio value="online">E-Signature</Radio>
                  <Radio value="offline">Offline</Radio>
                </Radio.Group>
              </Form.Item>
            </div>
          )}
          <div style={{ padding: "20px", backgroundColor: "white", marginBottom: "16px" }}>
          <div style={{ fontSize: "25px", marginBottom: 10 }}>Trial Details</div>
          <Form.Item
            className="item-width-50"
            label="Source of MSD Compound and Protocol Number/Identifier"
            name="MSDCompoundType"
            required
            rules={[{ required: true, message: "This is a required field" }]}
          >
            <Radio.Group
              onChange={MSDCompoundTypeSelection}
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "8px",
              }}
            >
              <Radio value="ODM">Standard source (ODM)</Radio>
              <Radio value="Other">Other source</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            className="item-width-50"
            label="MSD Compound and Protocol Number/Identifier"
            name="MSDCompound"
            required
            rules={[
              {
                required: true,
                whitespace: true,
                message: "This is a required field",
              },
              {
                pattern: /^(MK\d{4}[a-zA-Z]?|MC\d{4}[a-zA-Z]?|V\d{3}[a-zA-Z]?)-[a-zA-Z0-9]{3}$/,
                message:
                  'Invalid format. Please use MKXXXX-XXX, MKXXXXX-XXX, MCXXXX-XXX, MCXXXXX-XXX, VXXX-XXX, or VXXXX-XXX (e.g., MK1234-567, MK1234A-12A, MC1234, MC1234A, V123-AAA, V123A-1A2). Please note that if there are 5 characters following a compound with a "MK", "MC" prefix OR 4 letters following a "V" prefix, then the last character in the compound should be an alphabet character, as shown in the examples provided.',
              },
            ]}
          >
            {formRef?.getFieldsValue()?.MSDCompoundType === "ODM" ? (
              <Select
                showSearch
                placeholder="Type to search..."
                optionFilterProp="label"
                suffixIcon={msdDataLoading ? <Spin size="small" /> : null}
                options={MSDCompoundODMOptions?.map((option) => ({
                  label: option.value,
                  value: option.value,
                  key: option.studyNumber, // Ensure unique keys
                }))}
              />
            ) : (
              <Input />
            )}
          </Form.Item>
          <Form.Item
            className="item-width-50"
            label="Supplier"
            name="supplierOrganization"
            required
            rules={[{ required: true, message: "This is a required field" }]}
          >
            <Select
              showSearch
              placeholder="Type to search..."
              loading={supplierLoading}
              optionFilterProp="label"
              suffixIcon={supplierLoading ? <Spin size="small" /> : null}
              options={supplierOrgOptions}
            />
          </Form.Item>
          <Form.Item
            className="item-width-50"
            label="Supplier Compound and Protocol Number/Identifier"
            name="supplierCompound"
            required
            tooltip="Enter the Supplier’s Compound and Protocol number/identifier. If it is the same as MSD’s, re-enter it."
            rules={[
              {
                required: true,
                whitespace: true,
                message: "This is a required field",
              },
            ]}
          >
            <Input />
          </Form.Item>
          </div>
          <div style={{ padding: "20px", backgroundColor: "white", marginBottom: "16px" }}>
          <div style={{ fontSize: "25px", marginBottom: 10 }}>Transfer Information</div>
          <Form.Item
            className="item-width-50"
            label="Transfer ID"
            name="transferId"
            required
            rules={[{ required: true, message: "This is a required field" }]}
          >
            <Select
              options={transferIdOptions}
              onChange={handleTransferIdChange}
            />
          </Form.Item>
          <Form.Item
            className="item-width-80"
            label="DTS Version"
            name="DTSVersion"
            required
            tooltip="Enter a DTS version based on your last active version for this compound and transfer ID even if it was prior to using CDIG."
            rules={[
              { required: true, message: "This is a required field" },
              ({ getFieldValue }) => ({
                validator: (_, value) =>
                  validateDTSVersion(
                    value,
                    activeDTSVersion,
                    activeDTSVersionStatus,
                    isDTSVersionPresent,
                  ),
              }),
            ]}
            extra={
              <Form.Item shouldUpdate noStyle>
                {({ getFieldError, getFieldValue }) => {
                  setDtsFieldErrorCount(getFieldError("DTSVersion")?.length || 0);
                  return DTSTransferMessage(
                    disableDTS,
                    activeDTSVersion,
                    isDTSVersionPresent,
                    activeDTSVersionStatus,
                    getFieldError("DTSVersion")?.length > 0,
                    getFieldValue("DTSVersion"),
                  );
                }}
              </Form.Item>
            }
          >
            <InputNumber<string>
              min="1"
              step="0.0"
              stringMode
              className="custom-input-number"
              disabled={disableDTS}
            />
          </Form.Item>
          <Form.Item
            label="Description of Change"
            name="descriptionChange"
            required
            rules={[
              {
                required: true,
                whitespace: true,
                message: "This is a required field",
              },
            ]}
            extra={changeOfDescriptionMessage(formRef?.getFieldsValue()?.descriptionChange, disableChangeDescription)}
          >
            <Input disabled={disableChangeDescription} style={{ width: "50%" }}/>
          </Form.Item>
          <Form.Item
            className="purpose"
            name="purpose"
            required={false}
            rules={[
              {
                required: true,
                whitespace: true,
                message: "This is a required field",
              },
            ]}
            label={
              <div>
                <span className="custom-asterisk">*</span>Purpose
                <span className="purpose-helper-text">
                  Please use the following format:{" "}
                  <span style={{ fontStyle: "italic" }}>
                    &quot;The purpose of this Data Transfer Specifications (DTS)
                    document is to define the specifications associated with the
                    [...............] dataset and [...............] files for
                    [...............] analysis from [data supplier name] to
                    Merck Sharp & Dohme, Rahway, NJ, USA in support of [compound
                    name].&quot;
                  </span>
                </span>
              </div>
            }
          >
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item
            name="dataFormat"
            required={false}
            label="Data Format"
            tooltip="Upload a .pdf document only if this transfer includes a Data Format section. It will be appended to the DTS agreement as “APPENDIX B. Data Format.”"
            rules={[
              {
                validator: validateDataFormat(),
              },
            ]}
          >
            <Upload
              fileList={fileList}
              onChange={handleChange}
              beforeUpload={() => false}
              accept=".pdf"
            >
              <Button icon={<UploadOutlined />}>Upload</Button>
            </Upload>
          </Form.Item>
          <UploadDTS
            uploadDTSFormValue={uploadDTSFormValue}
            formRef={formRef}
            nextPage={nextPage}
            transferId={transferId}
            handleDeliverablesDescription={handleDeliverablesDescription}
            handleInitialization={handleInitialization}
            isInitialized={isInitialized}
            detailsSectionData={detailsSectionData}
          />
        </div>
        </div>
      ) : (
        <>
        <div style={{ padding: "20px", backgroundColor: "white", marginBottom: "16px" }}>
          <div style={{ fontSize: "25px", marginBottom: 10 }}>Trial Details</div>
          <Descriptions
            column={1}
            size="small"
            bordered
            items={trialDetailsFieldsValue}
          />
        </div>
        <div style={{ padding: "20px", backgroundColor: "white", marginBottom: "16px" }}>
        <div style={{ fontSize: "25px", marginBottom: 10 }}>Transfer Information</div>
        <Descriptions
          column={1}
          size="small"
          bordered
          items={transferInformationFieldsValue}
        />
        <div style={{ fontSize: "20px", marginBottom: 10, marginTop: 10 }}>Deliverables</div>
        <DeliverablesReview initialDeliverables={formValues} />
        </div>
        </>
      )}
      {contextHolder}
    </>
  );
};

export default Details;
